import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DsButtonVariants, DsModalService, DsModule } from '@levelaccess/design-system';
import { Observable } from 'rxjs';
import { CommonPipesModule } from '../../../../../../../shared/common-pipes.module';
import { FormService } from '../../../../../../../services/form.service';
import { ManualFindingFlagReason } from '../../../../../../../../../shared/constants/comment';
import { getFlagReasonTranslationKey } from '../../../../../../../../../shared/utils/comment.utility';
import { CustomValidators } from '../../../../../../../services/helpers/form-custom-validators';
import { ErrorMessageService } from '../../../../../../../services/error-message.service';

export interface IFlagCommentFormValue {
  flagReason?: ManualFindingFlagReason;
  message?: string;
}

enum FormFields {
  flagReason = 'flagReason',
  message = 'message',
}

interface IFlagForm {
  [FormFields.flagReason]: FormControl<ManualFindingFlagReason | null>;
  [FormFields.message]: FormControl<string>;
}

@Component({
  selector: 'app-add-finding-flag-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, CommonPipesModule, DsModule],
  templateUrl: './change-finding-flag-modal.component.html',
})
export class ChangeFindingFlagModalComponent implements OnInit {
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly FormFields: typeof FormFields = FormFields;

  public readonly flagReasonOptions: ManualFindingFlagReason[] = Object.values(ManualFindingFlagReason);
  public readonly getFlagReasonTranslationKey: typeof getFlagReasonTranslationKey = getFlagReasonTranslationKey;

  public addFlag: WritableSignal<boolean> = signal<boolean>(true);

  public form: FormGroup<IFlagForm> = new FormGroup<IFlagForm>({
    [FormFields.flagReason]: new FormControl<ManualFindingFlagReason | null>(null),
    [FormFields.message]: new FormControl<string>('', { nonNullable: true }),
  });

  public flagReasonErrorMessage$: Observable<string | null>;

  @Output()
  public confirmed: EventEmitter<IFlagCommentFormValue> = new EventEmitter<IFlagCommentFormValue>();

  constructor(
    public modal: NgbActiveModal,
    public modalService: DsModalService,
    private formService: FormService,
    private element: ElementRef<Element>,
    private errorMessageService: ErrorMessageService,
  ) {}

  public ngOnInit(): void {
    this.flagReasonErrorMessage$ = this.formService.buildFormControlErrorMessage$(this.form.get(FormFields.flagReason), 'reason');
  }

  public onFlagChange(): void {
    if (this.addFlag()) {
      const flagReasonControl = this.form.get(FormFields.flagReason);
      flagReasonControl.addValidators(CustomValidators.required);
      flagReasonControl.markAsTouched();
      flagReasonControl.updateValueAndValidity();
      if (flagReasonControl.invalid) {
        this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
        return;
      }
    }
    this.confirmed.emit({
      flagReason: this.form.get(FormFields.flagReason).value ?? undefined,
      message: this.form.get(FormFields.message).value ?? undefined,
    });
    this.modalService.closeAllModals();
  }
}
